import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject } from '@angular/core';
import { catchError, map, of, switchMap } from 'rxjs';
import { OrganizationService } from '../../services/organization.service';
import { OrganizationActions } from '../state/organization/organization.actions';
import { AuthActions } from '../state/auth/auth.actions';
import { errMessage } from '@ui';

export const loadActiveOrganization = createEffect(
  (
    actions$ = inject(Actions),
    organizationService = inject(OrganizationService),
  ) => {
    return actions$.pipe(
      ofType(OrganizationActions.getActiveOrganization),
      switchMap(() =>
        organizationService.getActiveOrganization().pipe(
          map((activeOrganization) =>
            OrganizationActions.setActiveOrganization({ activeOrganization }),
          ),
          catchError(() => [OrganizationActions.getActiveOrganizationFail()]),
        ),
      ),
    );
  },
  { functional: true },
);

export const loadMyOrganizations$ = createEffect(
  (
    actions$ = inject(Actions),
    organizationService = inject(OrganizationService),
  ) => {
    return actions$.pipe(
      ofType(OrganizationActions.getMyOrganizations),
      switchMap(() =>
        organizationService.getMyOrganizations().pipe(
          map((userOrganizations) =>
            OrganizationActions.getMyOrganizationsSuccess({
              userOrganizations: userOrganizations ?? [],
            }),
          ),
          catchError(() => [OrganizationActions.getMyOrganizationsFail()]),
        ),
      ),
    );
  },
  { functional: true },
);

/**
 * Loads only the active organization after the bearer token has already been
 * replaced. The requested id is carried through every terminal action so a
 * late response can never hydrate a newer switch.
 */
export const loadSwitchedActiveOrganization = createEffect(
  (
    actions$ = inject(Actions),
    organizationService = inject(OrganizationService),
  ) =>
    actions$.pipe(
      ofType(OrganizationActions.loadSwitchedActiveOrganization),
      switchMap(({ organizationId }) =>
        organizationService.getActiveOrganization().pipe(
          map((activeOrganization) => {
            if (activeOrganization._id !== organizationId) {
              return AuthActions.organizationDataLoadFailed({
                organizationId,
                message: 'The active organization does not match the switch.',
              });
            }

            return OrganizationActions.setSwitchedActiveOrganization({
              organizationId,
              activeOrganization,
            });
          }),
          catchError((error: unknown) =>
            of(
              AuthActions.organizationDataLoadFailed({
                organizationId,
                message:
                  error instanceof Error
                    ? errMessage(error)
                    : 'Unable to load the active organization.',
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);
